import { normalizeEtfRow, num, str } from "./etf-screener.normalize";
import {
  ETF_INDEX_RULEBOOKS,
  type EtfDetail,
  type EtfHolding,
  type EtfHoldingsResponse,
  type EtfIndexRulebook,
  type EtfRulebookWeights,
} from "./etf.type";

/**
 * One fund from the gateway's 32-key row, walking only declared keys, so a
 * column the pipeline adds upstream cannot reach the page unreviewed.
 */
export const normalizeEtfDetail = (raw: unknown): EtfDetail | null => {
  const row = normalizeEtfRow(raw);
  if (!row) return null;
  const r = raw as Record<string, unknown>;
  const rulebookWeights = Object.fromEntries(
    ETF_INDEX_RULEBOOKS.map((book) => [
      book,
      {
        compliantWeightPerc: num(r[`compliant_weight_perc_${book}`]),
        nonCompliantWeightPerc: num(r[`non_compliant_weight_perc_${book}`]),
      },
    ]),
  ) as Record<EtfIndexRulebook, EtfRulebookWeights>;
  return {
    ...row,
    rulebookWeights,
    holdingsFetchedAt: str(r.holdings_fetched_at),
  };
};

const bool = (value: unknown): boolean | null => (typeof value === "boolean" ? value : null);

const normalizeEtfHolding = (raw: unknown): EtfHolding | null => {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  return {
    holdingTicker: str(r.holding_ticker),
    holdingName: str(r.holding_name),
    weightPerc: num(r.weight_perc),
    screened: bool(r.screened),
    status: str(r.status),
    statusDjim: str(r.halal_status_djim),
    statusFtse: str(r.halal_status_ftse),
    statusMsci: str(r.halal_status_msci),
    statusSp: str(r.halal_status_sp),
  };
};

/**
 * The gateway's `{etf_ticker, as_of, total, limit, offset, holdings}`.
 * `holdings` may be null upstream for an empty basket; it is always an array
 * here. `limit` is read back, because the gateway clamps silently.
 */
export const normalizeEtfHoldingsResponse = (
  raw: unknown,
  requestedLimit: number,
): EtfHoldingsResponse => {
  const body = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const rows = Array.isArray(body.holdings) ? body.holdings : [];
  const holdings = rows
    .map(normalizeEtfHolding)
    .filter((row): row is EtfHolding => row !== null);
  return {
    etfTicker: str(body.etf_ticker),
    asOf: str(body.as_of),
    holdings,
    total: Math.max(0, num(body.total) ?? holdings.length),
    limit: Math.max(1, num(body.limit) ?? requestedLimit),
    offset: Math.max(0, num(body.offset) ?? 0),
  };
};

/**
 * A constituent's verdict is rated only when it is a real verdict.
 * null, ERROR_DATA and UNQUALIFIED (and a rulebook's INCOMPLETE_DATA) mean
 * we decline to rate it, never that it is compliant. Handover §B4.
 */
export const holdingIsRated = (status: string | null): status is string =>
  typeof status === "string" &&
  status.length > 0 &&
  status !== "ERROR_DATA" &&
  status !== "UNQUALIFIED" &&
  status !== "INCOMPLETE_DATA";
